import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { api, type ServerCapabilities } from '../services/api';

export function HomePage() {
  const { isAuthenticated } = useAuthStore();
  const [capabilities, setCapabilities] = useState<ServerCapabilities | null>(null);

  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    // #1074 D8: retry once on the first failure. The capabilities fetch races
    // with the dev/web container's nginx warmup on the very first unauthed
    // page load — a hard "Failed to load capabilities" toast for ~1.5s makes
    // the homepage look like the server is down. A single retry after 500ms
    // covers the typical warmup window without masking real outages.
    let retried = false;
    const load = () => {
      api.getCapabilities()
        .then((caps) => {
          setCapabilities(caps);
          setServerError(null);
        })
        .catch((err) => {
          if (!retried) {
            retried = true;
            setTimeout(load, 500);
            return;
          }
          console.error('Failed to load capabilities:', err);
          setServerError(err?.message ?? 'Cannot connect to server');
        });
    };
    load();
  }, []);

  const showLocalOption = capabilities?.localAnalysis &&
    (capabilities.authMode === 'local' || capabilities.authMode === 'both');

  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] px-4">
      {serverError && (
        <div className="w-full max-w-3xl mb-4 p-4 border border-red-200 dark:border-red-800 rounded bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300 text-center">
          {serverError}
        </div>
      )}
      <div className="max-w-3xl text-center">
        <h1 className="text-4xl md:text-6xl font-bold mb-6">
          Visualize Your Code Architecture
        </h1>
        <p className="text-xl text-muted-foreground mb-4">
          Generate beautiful architectural diagrams from your {showLocalOption ? 'local or GitHub' : 'GitHub'} repositories.
          Understand complex systems at a glance.
        </p>

        {showLocalOption && (
          <p className="text-base text-muted-foreground mb-6">
            No GitHub account required for local repository analysis
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {isAuthenticated ? (
            <Link
              to="/dashboard"
              className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-3 text-lg font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Go to Dashboard
            </Link>
          ) : (
            <Link
              to="/login"
              className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-3 text-lg font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Get Started
            </Link>
          )}
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-8">
          <div className="p-6 rounded-lg border border-border">
            <div className="text-3xl mb-4">🔍</div>
            <h3 className="text-lg font-semibold mb-2">Analyze</h3>
            <p className="text-muted-foreground">
              Point to any {showLocalOption ? 'local folder or GitHub' : 'GitHub'} repository and we&apos;ll analyze its architecture
            </p>
          </div>
          <div className="p-6 rounded-lg border border-border">
            <div className="text-3xl mb-4">📊</div>
            <h3 className="text-lg font-semibold mb-2">Visualize</h3>
            <p className="text-muted-foreground">
              See your code structure as interactive diagrams
            </p>
          </div>
          <div className="p-6 rounded-lg border border-border">
            <div className="text-3xl mb-4">📤</div>
            <h3 className="text-lg font-semibold mb-2">Export</h3>
            <p className="text-muted-foreground">
              Download diagrams as SVG for docs and presentations
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
